import React, {useCallback, useEffect, useState} from "react";
import {Grid, Tab, Tabs} from "@material-ui/core";
import {showSnackSuccess, toggleLoader} from "../../store/global/global-slice";
import {PublisherService} from "../../services/publisher.service";
import {useDispatch} from "react-redux";
import {NewPublisherBookShort} from "../../components/publisher/books/new-publisher-book-short";
import {EmptyBookShort} from "../../components/author/books/empty-book-short";
import {CustomTabPanel} from "../../components/ui-base/tab-panel";
import {PublisherSimilarBookMarketListModel} from "../../models/publisher/publisher-similar-book-market-list.model";
import {ListData, NewListData} from "../../models/view/list-data.model";
import {LastSearchData} from "../../models/view/last-search-data.model";
import {ApplicationStatusEnum} from "../../models/application-status.enum";
import {StyledButton} from "../../components/ui-base/button";
import {SearchBooks} from "../../components/publisher/books/search-books";
import {useTranslation} from "react-i18next";
import {
    ElasticFilterType,
    ElasticSearchSort,
    ElasticSearchType, SearchFilters, SearchForElastic
} from "../../components/publisher/books/search-for-elastic";
import {LastElasticSearchDataModel} from "../../models/view/last-elastic-search-data.model";

const PublisherMarketPage = () => {
    const [manuscriptsData, setManuscriptsData] = useState<ManuscriptsData>({newestManuscripts: {}, bookmarkManuscripts: {}, waitingManuscripts: {}} as ManuscriptsData);
    const [lastSearchData, setLastSearchData] = useState<LastElasticSearchDataModel>({searchType: ElasticSearchType.searchTypeTitle, searchValue: '', sortField: ElasticSearchSort.created, sortValue: 'desc', filters: {} as SearchFilters});
    const [loaded, setLoaded] = useState(false);
    const [tabActive, setTabActive] = React.useState(0);
    const dispatch = useDispatch();
    const { t } = useTranslation(['publisher', 'common']);

    const loadNewestManuscripts = useCallback(async (
        sortField: ElasticSearchSort,
        sortValue: 'asc'|'desc',
        searchType: ElasticSearchType,
        searchValue: string,
        filters: SearchFilters,
        page = 1,
        perPage = 12
    ) => {
        const newestManuscriptsLoaded = await PublisherService.getMarketApplicationSearch(
            sortField,
            sortValue,
            searchType,
            searchValue,
            filters,
            page,
            perPage,
            [ApplicationStatusEnum.Created, ApplicationStatusEnum.WithCont, ApplicationStatusEnum.Analyzed],
            false
        );

        setManuscriptsData(prevValue => ({
            ...prevValue,
            newestManuscripts: {
                items: (page > 1) ? [...prevValue.newestManuscripts.items, ...newestManuscriptsLoaded.results] : newestManuscriptsLoaded.results,
                nextPage: newestManuscriptsLoaded.next,
                total: newestManuscriptsLoaded.count
            }
        }));
    }, []);

    const loadBookmarkManuscripts = useCallback(async (
        sortField: ElasticSearchSort,
        sortValue: 'asc'|'desc',
        searchType: ElasticSearchType,
        searchValue: string,
        filters: SearchFilters,
        page = 1,
        perPage = 12
    ) => {
        const bookmarkManuscriptsLoaded = await PublisherService.getMarketApplicationSearch(
            sortField,
            sortValue,
            searchType,
            searchValue,
            filters,
            page,
            perPage,
            [ApplicationStatusEnum.Created, ApplicationStatusEnum.WithCont, ApplicationStatusEnum.Analyzed],
            true
        );

        setManuscriptsData(prevValue => ({
            ...prevValue,
            bookmarkManuscripts: {
                items: (page > 1) ? [...prevValue.bookmarkManuscripts.items, ...bookmarkManuscriptsLoaded.results] : bookmarkManuscriptsLoaded.results,
                nextPage: bookmarkManuscriptsLoaded.next,
                total: bookmarkManuscriptsLoaded.count
            }
        }));
    }, []);

    const loadWaitingManuscripts = useCallback(async (
        sortField: ElasticSearchSort,
        sortValue: 'asc'|'desc',
        searchType: ElasticSearchType,
        searchValue: string,
        filters: SearchFilters,
        page = 1,
        perPage = 12
    ) => {
        const waitingManuscriptsLoaded = await PublisherService.getMarketApplicationSearch(
            sortField,
            sortValue,
            searchType,
            searchValue,
            filters,
            page,
            perPage,
            [ApplicationStatusEnum.AskAccess]
        );

        setManuscriptsData(prevValue => ({
            ...prevValue,
            waitingManuscripts: {
                items: (page > 1) ? [...prevValue.waitingManuscripts.items, ...waitingManuscriptsLoaded.results] : waitingManuscriptsLoaded.results,
                nextPage: waitingManuscriptsLoaded.next,
                total: waitingManuscriptsLoaded.count
            }
        }));
    }, []);

    const loadManuscripts = useCallback(async (
        sortField: ElasticSearchSort,
        sortValue: 'asc'|'desc',
        searchType: ElasticSearchType,
        searchValue: string,
        filters: SearchFilters,
        page = 1,
        perPage = 12
    ) => {
        dispatch(toggleLoader(true));

        await loadNewestManuscripts(sortField, sortValue, searchType, searchValue, filters, page, perPage);
        await loadBookmarkManuscripts(sortField, sortValue, searchType, searchValue, filters, page, perPage);
        await loadWaitingManuscripts(sortField, sortValue, searchType, searchValue, filters, page, perPage);

        setLoaded(true);
        dispatch(toggleLoader(false));
    }, [dispatch, loadNewestManuscripts, loadBookmarkManuscripts, loadWaitingManuscripts]);

    const loadNextManuscripts = async (type: 'newest'|'bookmark'|'waiting') => {
        dispatch(toggleLoader(true));
        if (type === 'newest') {
            await loadNewestManuscripts(
                lastSearchData.sortField,
                lastSearchData.sortValue,
                lastSearchData.searchType,
                lastSearchData.searchValue,
                lastSearchData.filters,
                manuscriptsData.newestManuscripts.nextPage,
                12
            );
        } else if (type === 'bookmark') {
            await loadBookmarkManuscripts(
                lastSearchData.sortField,
                lastSearchData.sortValue,
                lastSearchData.searchType,
                lastSearchData.searchValue,
                lastSearchData.filters,
                manuscriptsData.bookmarkManuscripts.nextPage,
                12
            );
        } else if (type === 'waiting') {
            await loadWaitingManuscripts(
                lastSearchData.sortField,
                lastSearchData.sortValue,
                lastSearchData.searchType,
                lastSearchData.searchValue,
                lastSearchData.filters,
                manuscriptsData.waitingManuscripts.nextPage,
                12
            );
        }

        dispatch(toggleLoader(false));
    }

    useEffect(() => {
        loadManuscripts(
            lastSearchData.sortField,
            lastSearchData.sortValue,
            lastSearchData.searchType,
            lastSearchData.searchValue,
            lastSearchData.filters
        );
    }, [loadManuscripts]);

    const saveToBookmark = async (application: PublisherSimilarBookMarketListModel) => {
        const res = await PublisherService.marketAddToSave(application.id);

        if (res) {
            dispatch(showSnackSuccess(t('publisherMarket.saved')));
            loadManuscripts(
                lastSearchData.sortField,
                lastSearchData.sortValue,
                lastSearchData.searchType,
                lastSearchData.searchValue,
                lastSearchData.filters
            );
        }
    }

    const deleteFromBookmark = async (application: PublisherSimilarBookMarketListModel) => {
        const res = await PublisherService.marketRemoveFromSave(application.id);

        if (res) {
            dispatch(showSnackSuccess(t('publisherMarket.removed')));
            loadManuscripts(
                lastSearchData.sortField,
                lastSearchData.sortValue,
                lastSearchData.searchType,
                lastSearchData.searchValue,
                lastSearchData.filters
            );
        }
    }

    const askForPermission = async (application: PublisherSimilarBookMarketListModel) => {
        const res = await PublisherService.askForPermission(application.id);

        if (res) {
            dispatch(showSnackSuccess(t('publisherMarket.askAccess')));
            loadManuscripts(
                lastSearchData.sortField,
                lastSearchData.sortValue,
                lastSearchData.searchType,
                lastSearchData.searchValue,
                lastSearchData.filters
            );
        }
    }

    const loadDataFromSearch = (
        sortField: ElasticSearchSort,
        sortValue: 'asc'|'desc',
        searchType: ElasticSearchType,
        searchValue: string,
        filters: SearchFilters
    ) => {
        setLastSearchData({
            sortField: sortField,
            sortValue: sortValue,
            searchType: searchType,
            searchValue: searchValue,
            filters: filters
        });

        loadManuscripts(
            sortField,
            sortValue,
            searchType,
            searchValue,
            filters,
            1,
            12
        );
    }

    return (
        <Grid container direction={"column"}>
            <SearchForElastic
                loadData={loadDataFromSearch}
                searchTypeOptions={[ElasticSearchType.searchTypeTitle, ElasticSearchType.searchTypeAuthor]}
                sortTypeOptions={[ElasticSearchSort.created, ElasticSearchSort.matching]}
                filterOptions={[ElasticFilterType.tags, ElasticFilterType.categories]}
                showSort={true}
                showFilter={true}
            />

            <Grid item>
                <Tabs value={tabActive} onChange={(event, newValue) => { setTabActive(newValue) }}>
                    <Tab label={t('publisherMarket.newestManu') + ' (' + (manuscriptsData.newestManuscripts?.total || 0) + ')'}/>
                    <Tab label={t('publisherMarket.savedManu') + ' (' + (manuscriptsData.bookmarkManuscripts?.total || 0) + ')'}/>
                    <Tab label={t('publisherMarket.waitingManu') + ' (' + (manuscriptsData.waitingManuscripts?.total || 0) + ')'}/>
                </Tabs>

                <CustomTabPanel value={tabActive} index={0}>
                    <Grid item container direction={"row"} spacing={2}>
                        {manuscriptsData.newestManuscripts?.items?.map((application: PublisherSimilarBookMarketListModel, index) => (
                            <NewPublisherBookShort key={index} manuscript={application} market={true} saveToBookmark={() => {saveToBookmark(application)}} askForPermission={() => {askForPermission(application)}}/>
                        ))}
                    </Grid>

                    {manuscriptsData.newestManuscripts?.nextPage &&
                    <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                        <StyledButton type={"button"} color={"transparent"} onClick={() => {loadNextManuscripts('newest')}}>
                            {t('publisherMarket.showNext')}
                        </StyledButton>
                    </Grid>
                    }

                    {manuscriptsData.newestManuscripts?.items?.length === 0 && loaded &&
                    <EmptyBookShort
                        title={t('publisherMarket.empty')}
                        description={''}
                        showButton={false}
                    />
                    }
                </CustomTabPanel>

                <CustomTabPanel value={tabActive} index={1}>
                    <Grid item container direction={"row"} spacing={2}>
                        {manuscriptsData.bookmarkManuscripts?.items?.map((application: PublisherSimilarBookMarketListModel, index) => (
                            <NewPublisherBookShort key={index} manuscript={application} market={true} deleteFromBookmark={() => {deleteFromBookmark(application)}} askForPermission={() => {askForPermission(application)}}/>
                        ))}
                    </Grid>

                    {manuscriptsData.bookmarkManuscripts?.nextPage &&
                    <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                        <StyledButton type={"button"} color={"transparent"} onClick={() => {loadNextManuscripts('bookmark')}}>
                            {t('publisherMarket.showNext')}
                        </StyledButton>
                    </Grid>
                    }

                    {manuscriptsData.bookmarkManuscripts?.items?.length === 0 && loaded &&
                    <EmptyBookShort
                        title={t('publisherMarket.empty')}
                        description={''}
                        showButton={false}
                    />
                    }
                </CustomTabPanel>

                <CustomTabPanel value={tabActive} index={2}>
                    <Grid item container direction={"row"} spacing={2}>
                        {manuscriptsData.waitingManuscripts?.items?.map((application: PublisherSimilarBookMarketListModel, index) => (
                            <NewPublisherBookShort key={index} manuscript={application} market={true}/>
                        ))}
                    </Grid>

                    {manuscriptsData.waitingManuscripts?.nextPage &&
                    <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                        <StyledButton type={"button"} color={"transparent"} onClick={() => {loadNextManuscripts('waiting')}}>
                            {t('publisherMarket.showNext')}
                        </StyledButton>
                    </Grid>
                    }

                    {manuscriptsData.waitingManuscripts?.items?.length === 0 && loaded &&
                    <EmptyBookShort
                        title={t('publisherMarket.empty')}
                        description={''}
                        showButton={false}
                    />
                    }
                </CustomTabPanel>
            </Grid>
        </Grid>
    );
}

export default PublisherMarketPage;

interface ManuscriptsData {
    newestManuscripts: NewListData,
    bookmarkManuscripts: NewListData,
    waitingManuscripts: NewListData
}
